import React, { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import Box from '@mui/material/Box';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import Stack from '@mui/material/Stack';
import Switch from '@mui/material/Switch';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';

const buildGroups = (samples) => {
  const groupsByName = new Map();

  samples.forEach((sample) => {
    const existing = groupsByName.get(sample.name);
    if (existing) {
      existing.ids.push(sample.id);
      return;
    }

    groupsByName.set(sample.name, {
      name: sample.name,
      ids: [sample.id]
    });
  });

  return [...groupsByName.values()].sort((left, right) => left.name.localeCompare(right.name));
};

function SampleFilterList({
  samples,
  selectedSampleNames,
  onChange,
  disabled,
  showSingletons,
  onShowSingletonsChange,
  singletonSampleNames
}) {
  const [searchQuery, setSearchQuery] = useState('');

  const groups = useMemo(() => buildGroups(samples), [samples]);
  const filteredGroups = useMemo(() => (
    showSingletons
      ? groups
      : groups.filter((group) => !singletonSampleNames.has(group.name))
  ), [groups, showSingletons, singletonSampleNames]);
  const normalizedQuery = searchQuery.trim().toLowerCase();
  const visibleGroups = useMemo(() => {
    if (normalizedQuery.length === 0) {
      return filteredGroups;
    }

    return filteredGroups.filter((group) => group.name.toLowerCase().includes(normalizedQuery));
  }, [filteredGroups, normalizedQuery]);

  const selectedFilteredCount = filteredGroups.filter((group) => selectedSampleNames.has(group.name)).length;
  const selectedVisibleCount = visibleGroups.filter((group) => selectedSampleNames.has(group.name)).length;
  const allVisibleSelected = visibleGroups.length > 0 && selectedVisibleCount === visibleGroups.length;
  const someVisibleSelected = selectedVisibleCount > 0 && !allVisibleSelected;

  const updateNameSelection = (name, checked) => {
    const next = new Set(selectedSampleNames);
    if (checked) {
      next.add(name);
    } else {
      next.delete(name);
    }
    onChange(next);
  };

  const setAllVisible = (checked) => {
    const next = new Set(selectedSampleNames);
    visibleGroups.forEach((group) => {
      if (checked) {
        next.add(group.name);
      } else {
        next.delete(group.name);
      }
    });
    onChange(next);
  };

  return (
    <Box
      sx={{
        border: 1,
        borderColor: 'divider',
        borderRadius: 1,
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        flex: 1,
        minHeight: 0
      }}
    >
      <Stack spacing={1} sx={{ p: 1.5, borderBottom: 1, borderColor: 'divider', flexShrink: 0 }}>
        <Typography variant="subtitle2">
          {`Samples (${selectedFilteredCount}/${filteredGroups.length} selected)`}
        </Typography>
        <FormControlLabel
          sx={{ m: 0 }}
          control={(
            <Switch
              size="small"
              checked={showSingletons}
              onChange={(event) => onShowSingletonsChange(event.target.checked)}
              disabled={disabled}
            />
          )}
          label="Show singletons"
        />
        <TextField
          size="small"
          placeholder="Search sample name"
          value={searchQuery}
          onChange={(event) => setSearchQuery(event.target.value)}
          disabled={disabled}
        />
        <FormControlLabel
          sx={{ m: 0 }}
          control={(
            <Checkbox
              size="small"
              checked={allVisibleSelected}
              indeterminate={someVisibleSelected}
              onChange={(event) => setAllVisible(event.target.checked)}
              disabled={disabled || visibleGroups.length === 0}
            />
          )}
          label={normalizedQuery.length === 0
            ? `Select all (${filteredGroups.length})`
            : `Select all matching (${visibleGroups.length})`}
        />
      </Stack>

      <Box sx={{ flex: 1, minHeight: 0, overflow: 'auto', p: 1 }}>
        {visibleGroups.length === 0 && (
          <Typography variant="caption" color="text.secondary">
            {filteredGroups.length === 0 ? 'No samples available.' : 'No samples match this search.'}
          </Typography>
        )}
        {visibleGroups.map((group) => (
          <FormControlLabel
            key={group.name}
            sx={{ m: 0, width: '100%', alignItems: 'center' }}
            control={(
              <Checkbox
                size="small"
                checked={selectedSampleNames.has(group.name)}
                onChange={(event) => updateNameSelection(group.name, event.target.checked)}
                disabled={disabled}
              />
            )}
            label={
              <Typography
                variant="caption"
                noWrap
                title={group.ids.length > 1 ? `${group.name} (${group.ids.length} samples)` : group.name}
              >
                {group.ids.length > 1 ? `${group.name} (x${group.ids.length})` : group.name}
              </Typography>
            }
          />
        ))}
      </Box>
    </Box>
  );
}

SampleFilterList.propTypes = {
  samples: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.number.isRequired,
    name: PropTypes.string.isRequired
  })).isRequired,
  selectedSampleNames: PropTypes.instanceOf(Set).isRequired,
  onChange: PropTypes.func.isRequired,
  showSingletons: PropTypes.bool.isRequired,
  onShowSingletonsChange: PropTypes.func.isRequired,
  singletonSampleNames: PropTypes.instanceOf(Set).isRequired,
  disabled: PropTypes.bool
};

SampleFilterList.defaultProps = {
  disabled: false
};

export default SampleFilterList;
